import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';

export const coachingRecapUrlPath = (repUserId: string) =>
	`aida/coffee/coaching/recap/${encodeURIComponent(repUserId)}`;

interface UseCoachingRecapMutationOptions {
	onSuccess?: () => void;
	onError?: (error: Api.IOperationResultNoValue) => void;
}

interface CoachingRecapMutationParams {
	repUserId: string;
	impersonationContext?: Api.IImpersonationContext;
}

/**
 * Queues today's coaching recap for one rep and emails it to the caller. Returns as soon as the run
 * is queued; the recap is built in a background job.
 *
 * The rep id is the whole request — it rides in the route and there is no body. The run is always
 * today, and the rep card's score and length filters are applied client-side (the backend rejects
 * those criteria), so the set a manager is looking at is not something the server could reproduce.
 * It selects its own population instead.
 */
export function useCoachingRecapMutation({ onSuccess, onError }: UseCoachingRecapMutationOptions = {}) {
	const userSession = useUserSession();

	return useMutation<void, Api.IOperationResultNoValue, CoachingRecapMutationParams>({
		mutationFn: ({ repUserId, impersonationContext }: CoachingRecapMutationParams) => {
			return userSession.webServiceHelper.callAsync<void>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: coachingRecapUrlPath(repUserId),
				}),
				'POST'
			);
		},
		onSuccess,
		onError,
	});
}
